import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useMealPlanningStore } from '@/stores/meal-planning-store'
import { useAuthStore } from '@/stores/auth-store'
import { useHouseholdStore } from '@/stores/household-store'
import { LoginModal } from '@/components/auth/login-modal'
import { Recipe } from '@/types/recipe'
import { 
  CalendarDays, 
  Plus, 
  CheckCircle,
  AlertCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface MealPlanningWidgetProps {
  recipe: Recipe
  className?: string
  compact?: boolean
}

interface WeekDay {
  date: Date
  dateStr: string
  dayName: string
  dayNumber: number
  hasPlannedMeal: boolean
  plannedMealTitle?: string
  isToday: boolean
}

export function MealPlanningWidget({ recipe, className = '' }: MealPlanningWidgetProps) {
  const { user } = useAuthStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const {
    currentWeekStart,
    currentMealPlan,
    getMealsForDate,
    addPlannedMeal,
    loadMealPlans,
    ensureDefaultMealPlan,
    setCurrentMealPlan,
    loading: mealPlanLoading
  } = useMealPlanningStore()

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Initialize meal planning when component mounts
  useEffect(() => {
    if (user) {
      void loadMealPlans(viewMode === 'household' ? currentHousehold?.id : undefined)
    }
  }, [currentHousehold?.id, loadMealPlans, user, viewMode])

  // Generate week days for display
  const getWeekDays = (): WeekDay[] => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const weekStart = new Date(currentWeekStart)
    const days: WeekDay[] = []
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart)
      date.setDate(weekStart.getDate() + i)
      
      const dateStr = date.toISOString().split('T')[0]
      const meals = getMealsForDate(dateStr)
      const dinnerMeal = meals.find(meal => meal.meal_type === 'diner')
      
      days.push({
        date,
        dateStr,
        dayName: ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'][i],
        dayNumber: date.getDate(),
        hasPlannedMeal: !!dinnerMeal,
        plannedMealTitle: dinnerMeal?.recipe?.title,
        isToday: date.getTime() === today.getTime()
      })
    }
    
    return days
  }

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 3000)
  }

  // Ensure user has a meal plan
  const ensureMealPlan = async () => {
    if (currentMealPlan) {
      return currentMealPlan
    }

    const mealPlan = await ensureDefaultMealPlan(viewMode === 'household' ? currentHousehold?.id : undefined)
    if (mealPlan) {
      setCurrentMealPlan(mealPlan)
      return mealPlan
    }

    return null
  }

  // Add recipe to specific date
  const handleAddToDate = async (dateStr: string, dayName: string) => {
    if (!user) {
      setShowLoginModal(true)
      return
    }

    setLoading(true)
    try {
      const mealPlan = await ensureMealPlan()
      if (!mealPlan) {
        showMessage('error', 'Kon geen maaltijdplan aanmaken')
        return
      }

      const success = await addPlannedMeal({
        meal_plan_id: mealPlan.id,
        recipe_id: recipe.id,
        planned_date: dateStr,
        meal_type: 'diner',
        planned_servings: recipe.servings || 4,
        notes: `Gepland vanuit recept: ${recipe.title}`,
        assigned_to: null,
      })

      if (success) {
        showMessage('success', `${recipe.title} gepland voor ${dayName}`)
      } else {
        showMessage('error', 'Kon recept niet plannen')
      }
    } catch (err) {
      showMessage('error', 'Er ging iets mis bij het plannen')
    } finally {
      setLoading(false)
    }
  }

  // Quick add to today
  const handleAddToday = () => {
    const today = new Date().toISOString().split('T')[0]
    handleAddToDate(today, 'vandaag')
  }

  // Quick add to tomorrow
  const handleAddTomorrow = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const tomorrowStr = tomorrow.toISOString().split('T')[0]
    handleAddToDate(tomorrowStr, 'morgen')
  }

  const weekDays = getWeekDays()

  if (!user) {
    return (
      <>
        <Card className={className}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="h-4 w-4" />
              Weekplanning
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-600 mb-3">
              Log in om dit recept aan je weekplanning toe te voegen
            </p>
            <Button onClick={() => setShowLoginModal(true)} size="sm">
              Inloggen
            </Button>
          </CardContent>
        </Card>
        
        <LoginModal 
          open={showLoginModal} 
          onOpenChange={setShowLoginModal} 
        />
      </>
    )
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="h-4 w-4" />
            Weekplanning
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Actions */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddToday}
                disabled={loading}
                className="flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Vandaag
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddTomorrow}
                disabled={loading}
                className="flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Morgen
              </Button>
            </div>
          </div>

          {/* Week Overview */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Deze week</h4>
            <div className="grid grid-cols-7 gap-1">
              {weekDays.map((day) => (
                <button
                  key={day.dateStr}
                  onClick={() => handleAddToDate(day.dateStr, day.dayName)}
                  disabled={loading}
                  className={cn(
                    "p-2 text-xs rounded border transition-colors",
                    day.isToday && "ring-2 ring-orange-500 ring-opacity-50",
                    day.hasPlannedMeal
                      ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                      : "bg-gray-50 border-gray-200 hover:bg-gray-100",
                    loading && "opacity-50 cursor-not-allowed"
                  )}
                  title={day.hasPlannedMeal ? `${day.plannedMealTitle}` : `Plan voor ${day.dayName} ${day.dayNumber}`}
                >
                  <div className="font-medium">{day.dayName}</div>
                  <div className="text-xs text-gray-500">{day.dayNumber}</div>
                  {day.hasPlannedMeal ? (
                    <CheckCircle className="h-3 w-3 mx-auto mt-1 text-green-600" />
                  ) : (
                    <Plus className="h-3 w-3 mx-auto mt-1 text-gray-400" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Loading State */}
          {(loading || mealPlanLoading) && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500"></div>
              Plannen...
            </div>
          )}

          {/* Message */}
          {message && (
            <div className={cn(
              "flex items-center gap-2 p-2 rounded text-xs",
              message.type === 'success' 
                ? "text-green-700 bg-green-50" 
                : "text-red-700 bg-red-50"
            )}>
              {message.type === 'success' ? (
                <CheckCircle className="h-3 w-3" />
              ) : (
                <AlertCircle className="h-3 w-3" />
              )}
              {message.text}
            </div>
          )}
        </CardContent>
      </Card>

      <LoginModal 
        open={showLoginModal} 
        onOpenChange={setShowLoginModal} 
      />
    </>
  )
}
